import React, { MutableRefObject, useEffect, useRef, useState } from 'react';
import { useDispatch } from 'react-redux';
import _ from 'lodash';
import { Formik, Field, FormikState, FormikProps } from 'formik';
import { Modal as BootstrapModal, Button } from 'react-bootstrap';
import { FormGroup, TextboxField, Modal } from '@tradingblock/components';
import { AccountManagementDataActions } from '../../../data/global/actions/AccountManagementActions';
import { useStateSelector } from '../../../data/global/dataSelectors';
import { getInvalidError, getMismatchError, getRequiredError, getStaleError } from './Validation';
import { EmailUpdateValueProps, RE_EMAIL, SubmitEmailValues } from '@tradingblock/types';

export const EmailUpdate: React.FunctionComponent<{
  show?: boolean;
  toggleModal: () => void;
  accountHolderIndex: number;
}> = ({ show, toggleModal, accountHolderIndex }) => {

  const accountManagementDetails = useStateSelector(s => s.accountManagement.accountManagementDetails.details);
  const { accountHolders } = accountManagementDetails;
  const user = accountHolders[accountHolderIndex];
  const email = user.email;
  const initialEmailUpdateValues: EmailUpdateValueProps = {
    emailValue: email ? email : '',
    emailConfirm: '',
  };
  const accountId = useStateSelector(s => s.accounts.account && s.accounts.account.AccountId);
  const dispatch = useDispatch();
  const updateAccountResponseCode = useStateSelector(s => s.accountManagement.update.responseCode);
  const twoFactorAuthenticationDetails = useStateSelector(s => s.twoFactorAuthentication.details);
  const isUpdatingAccountData = useStateSelector(s => s.accountManagement.update.isLoading);
  const [errorMsg, setErrorMsg] = useState<string | null>(null);
  const formikRef = (useRef<FormikProps<EmailUpdateValueProps> | null>(null)) as ((instance: any) => void) & MutableRefObject<FormikProps<EmailUpdateValueProps> | null>;

  const onValidate = (values: EmailUpdateValueProps) => {
    const { emailValue, emailConfirm } = values;

    if (!emailValue) {
      return {
        [`emailValue`]: getRequiredError(emailValue),
        [`emailConfirm`]: getRequiredError(emailConfirm),
      };
    }

    if (!emailConfirm) {
      return {
        [`emailValue`]: getRequiredError(emailValue),
        [`emailConfirm`]: getRequiredError(emailConfirm),
      };
    }

    if (!RE_EMAIL.test(emailValue)) {
      return {
        [`emailValue`]: getInvalidError(RE_EMAIL.test(emailValue)),
        [`emailConfirm`]: getInvalidError(RE_EMAIL.test(emailConfirm)),
      };
    }

    if (!RE_EMAIL.test(emailConfirm)) {
      return {
        [`emailValue`]: getInvalidError(RE_EMAIL.test(emailValue)),
        [`emailConfirm`]: getInvalidError(RE_EMAIL.test(emailConfirm)),
      };
    }

    if (emailValue !== emailConfirm) {
      return {
        [`emailConfirm`]: getMismatchError(emailConfirm === emailValue),
      };
    }

    if (emailValue === email) {
      return {
        [`emailConfirm`]: getStaleError(emailValue !== email),
      };
    }
  };

  const onSubmit = (values: EmailUpdateValueProps) => {
    const { emailValue } = values;
    const updateValues: SubmitEmailValues = {
      item: 'Email',
      accountHolder: {
        email: emailValue,
      },
      holder: accountHolderIndex,
    };

    dispatch(
      AccountManagementDataActions.requestMakeAccountUpdate({
        accountId: accountId,
        request: updateValues,
      })
    );
  };
  
  useEffect(() => {
    if (updateAccountResponseCode === 0 && show) {
      formikRef && formikRef.current && formikRef.current.resetForm();
      toggleModal();
      dispatch(AccountManagementDataActions.resetMakeAccountUpdateResponseCode());
    }  
  }, [updateAccountResponseCode, show]);

  const onClose = (resetForm: (nextState?: Partial<FormikState<EmailUpdateValueProps>> | undefined) => void) => {
    if (show) {
      resetForm();
      dispatch(AccountManagementDataActions.resetMakeAccountUpdateResponseCode());
    }
  }
  
  useEffect(() => {
    if (updateAccountResponseCode === 213) {
      setErrorMsg('This email is currently verified for two-factor authentication (2FA). Please add another two-factor authentication method before updating your email as we require at least one verified two-factor authentication method at all times.');
    } else {
      setErrorMsg(null);
    }
  }, [updateAccountResponseCode]);

  return (
    <Formik
      initialValues={initialEmailUpdateValues}
      onSubmit={onSubmit}
      validate={onValidate}
      validateOnChange={true}
      innerRef={formikRef}
    >
      {({ handleSubmit, resetForm }) => (
        <Modal
          setShow={() => {
            onClose(resetForm);
            toggleModal();
          }}
          show={show}
        >
          <BootstrapModal.Header closeButton>
            <BootstrapModal.Title>Email Update</BootstrapModal.Title>
          </BootstrapModal.Header>
          <BootstrapModal.Body>
            <form id="emailUpdate" onSubmit={handleSubmit}>
              <FormGroup>
                <Field component={TextboxField} name={'emailValue'} id={'emailValue'} label="Enter new email" />
              </FormGroup>
              <FormGroup>
                <Field component={TextboxField} name={'emailConfirm'} id={'emailConfirm'} label="Confirm new email" />
              </FormGroup>
            </form>
            <p className="txt-sm mute">
              Upon submission, you will receive an email notification to accept or deny this update. If confirmed, all
              updates are pending admin review. You will be notified when your submission has been processed.
            </p>
            {
              !twoFactorAuthenticationDetails && accountHolderIndex === 0 &&
              (
                <p className="txt-sm mute warning mb-0">If this email is used for two-factor authentication (2FA), please re-verify two-factor authentication after changing your email.</p>
              )
            }
            {twoFactorAuthenticationDetails && twoFactorAuthenticationDetails.isEmailVerified && accountHolderIndex === 0 && <p className="txt-sm mute warning mb-0">The current email on your account is verified for two-factor authentication (2FA). Once your email change request is complete, you will need to re-verify two-factor authentication with your new email.</p>}
          </BootstrapModal.Body>
          <BootstrapModal.Footer className="txt-sm flex-direction-col">
            <div className="d-flex align-items-center justify-content-space-between w-100">
              <Button
                variant="secondary"
                onClick={() => {
                  resetForm();
                  toggleModal();
                  dispatch(AccountManagementDataActions.resetMakeAccountUpdateResponseCode());
                }}
              >
                Cancel
              </Button>
              <Button
                variant="primary"
                onClick={(values: any) => handleSubmit(values)}
                disabled={isUpdatingAccountData}
              >
                Submit
              </Button>
            </div>
            {errorMsg && <p className="mt-3 mb-0 error w-100 text-align-center">{errorMsg}</p>}
          </BootstrapModal.Footer>
        </Modal>
      )}
    </Formik>
  );
};
